import { useCallback, useEffect, useRef, useState } from "react";
import { Music, Pause, Play } from "lucide-react";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import type { VideoElementData } from "@/types/storyboard";

export const formatTime = (totalSeconds: number): string => {
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return "0:00";
  }
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const buildHiddenEmbedSrc = (media: VideoElementData): string => {
  if (media.provider === "YOUTUBE") {
    const params = new URLSearchParams({
      enablejsapi: "1",
      playsinline: "1",
      controls: "0",
      origin: window.location.origin,
    });
    return `https://www.youtube-nocookie.com/embed/${media.videoId}?${params.toString()}`;
  }
  const params = new URLSearchParams({ dnt: "1", api: "1" });
  if (media.embedHash) {
    params.set("h", media.embedHash);
  }
  return `https://player.vimeo.com/video/${media.videoId}?${params.toString()}`;
};

/**
 * Drives a hidden YouTube/Vimeo iframe through the providers' postMessage
 * protocols. Audio elements are produced as videos with a still image,
 * but participants get audio-player UX (thumbnail + play/seek controls).
 */
const useEmbedAudioPlayer = (media: VideoElementData) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const post = useCallback((message: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(JSON.stringify(message), "*");
  }, []);

  const isYoutube = media.provider === "YOUTUBE";

  useEffect(() => {
    const markReady = () => {
      if (!readyRef.current) {
        readyRef.current = true;
        setReady(true);
      }
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      let data: Record<string, unknown>;
      try {
        data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (!data || typeof data !== "object") {
        return;
      }

      if (isYoutube) {
        if (data.event === "onReady" || data.event === "initialDelivery") {
          markReady();
        }
        if (data.event === "infoDelivery" && data.info) {
          markReady();
          const info = data.info as Record<string, unknown>;
          if (typeof info.currentTime === "number") {
            setCurrentTime(info.currentTime);
          }
          if (typeof info.duration === "number" && info.duration > 0) {
            setDuration(info.duration);
          }
          if (typeof info.playerState === "number") {
            setPlaying(info.playerState === 1);
          }
        }
        return;
      }

      // Vimeo (legacy postMessage API, enabled via api=1)
      const payload = data.data as Record<string, unknown> | undefined;
      switch (data.event) {
        case "ready":
          markReady();
          post({ method: "addEventListener", value: "playProgress" });
          post({ method: "addEventListener", value: "play" });
          post({ method: "addEventListener", value: "pause" });
          post({ method: "addEventListener", value: "finish" });
          post({ method: "getDuration" });
          break;
        case "playProgress":
          if (payload && typeof payload.seconds === "number") {
            setCurrentTime(payload.seconds);
          }
          if (payload && typeof payload.duration === "number") {
            setDuration(payload.duration);
          }
          break;
        case "play":
          setPlaying(true);
          break;
        case "pause":
          setPlaying(false);
          break;
        case "finish":
          setPlaying(false);
          setCurrentTime(0);
          break;
        default:
          break;
      }
      if (data.method === "getDuration" && typeof data.value === "number") {
        setDuration(data.value);
      }
    };

    window.addEventListener("message", onMessage);

    // YouTube requires a "listening" handshake before it emits state
    let handshake: number | undefined;
    if (isYoutube) {
      handshake = window.setInterval(() => {
        if (readyRef.current) {
          window.clearInterval(handshake);
          return;
        }
        post({ event: "listening", id: media.videoId, channel: "widget" });
      }, 400);
    }

    return () => {
      window.removeEventListener("message", onMessage);
      if (handshake) {
        window.clearInterval(handshake);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [media.provider, media.videoId, media.embedHash]);

  const toggle = useCallback(() => {
    if (isYoutube) {
      post({
        event: "command",
        func: playing ? "pauseVideo" : "playVideo",
        args: [],
      });
    } else {
      post({ method: playing ? "pause" : "play" });
    }
  }, [isYoutube, playing, post]);

  const seek = useCallback(
    (seconds: number) => {
      setCurrentTime(seconds);
      if (isYoutube) {
        post({ event: "command", func: "seekTo", args: [seconds, true] });
      } else {
        post({ method: "setCurrentTime", value: seconds });
      }
    },
    [isYoutube, post]
  );

  return { iframeRef, ready, playing, currentTime, duration, toggle, seek };
};

const AudioPlayerCard = ({ media }: { media: VideoElementData }) => {
  const { iframeRef, ready, playing, currentTime, duration, toggle, seek } =
    useEmbedAudioPlayer(media);

  const thumbnail =
    media.thumbnailUrl ||
    (media.provider === "YOUTUBE"
      ? `https://img.youtube.com/vi/${media.videoId}/hqdefault.jpg`
      : undefined);

  return (
    <div className="w-full rounded-lg border bg-card p-4">
      <div className="flex items-center gap-4">
        <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted">
          {thumbnail ? (
            <img
              src={thumbnail}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <Music className="h-8 w-8 text-muted-foreground" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          {media.title && (
            <div className="mb-2 truncate font-semibold">{media.title}</div>
          )}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={toggle}
              disabled={!ready}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label={playing ? "Pause" : "Abspielen"}
            >
              {playing ? (
                <Pause className="h-5 w-5" />
              ) : (
                <Play className="ml-0.5 h-5 w-5" />
              )}
            </button>
            <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {formatTime(currentTime)}
            </span>
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={1}
              value={Math.min(currentTime, duration || 0)}
              onChange={(e) => seek(Number(e.target.value))}
              disabled={!ready || !duration}
              className="h-1.5 flex-1 cursor-pointer accent-primary disabled:cursor-not-allowed"
              aria-label="Fortschritt"
            />
            <span className="w-10 shrink-0 text-xs tabular-nums text-muted-foreground">
              {formatTime(duration)}
            </span>
          </div>
        </div>
      </div>
      {/* Hidden provider iframe that actually plays the audio track */}
      <div className="h-0 w-0 overflow-hidden" aria-hidden="true">
        <iframe
          ref={iframeRef}
          src={buildHiddenEmbedSrc(media)}
          title={media.title || `${media.provider} Audio`}
          allow="autoplay; encrypted-media"
          tabIndex={-1}
        />
      </div>
    </div>
  );
};

interface Props {
  audio: VideoElementData;
}

export const AudioElement = ({ audio }: Props) => {
  if (!audio?.videoId || !audio?.provider) {
    return null;
  }

  return (
    <div className="prose max-w-full">
      {audio.title && <h2>{audio.title}</h2>}
      {audio.introduction && (
        <CourseMarkdown>{audio.introduction}</CourseMarkdown>
      )}
      {/* Same 640px cap as video and labeled graphics */}
      <div className="not-prose max-w-160">
        <AudioPlayerCard
          key={`${audio.provider}-${audio.videoId}`}
          media={audio}
        />
      </div>
    </div>
  );
};

export default AudioElement;
